import {
  Component, ViewChild, OnInit, isDevMode, ViewEncapsulation, TemplateRef, ChangeDetectorRef, OnDestroy
} from '@angular/core';
import { StarbaseLoginService, IUserTokenModel, UserTokenModel } from 'starbase-login';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { StarbaseModalOverlayService } from 'modal-overlay';
import { StarbaseModalButton, StarbaseModalDialogService, StarbaseModalOptions, StarbaseModalTypes } from 'starbase-modal-dialog';
import { GalleryTypeService } from './services/gallery-type.service';
import { debounceTime, filter, finalize, map, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { LookupDataService } from './services/lookup-data.service';
import { AppInsightsService } from './app-insights.service';
// import { HelpDataModel, StarbaseHelpDeskComponent } from 'starbase-help-desk';
import { HelpDeskService } from './services/help-desk.service';
import {
  StarbaseNotificationService, StarbaseNotificationHistoryComponent,
  NotificationStyleMode, StarbaseNotificationTypes, StarbaseNotificationSentiment
} from 'starbase-notification';
import { routeFade } from './animations';
import { UserDataService } from './services/user-data.service';
import { ContactDataService } from './services/contact-dash.service';
import { StarbaseComponentMessageService } from './services/starbase-component-message.service';
import { AccountTabs, PasswordScoreEnum } from 'account-management';
import { FilterParams, UserAccountModel, FormatDecimalString, IUserAccountModel, ContactModel } from 'starbase-launcher';
import { Log_ext } from './models/log-ext';
import { LogDataService } from './services/log-data.service';
import { NewTableEditorUserViewOptions } from 'new-table-editor';
import { LuSubscriptionPriceService } from './services/lu-subscriptionprice.service';
import { LU_SubscriptionPriceModel_ext } from './models/lu_subscriptionprice';
import { AppGlobalsService } from './app-globals.service';
import { TransactionLogService } from './services/transactionlog-data.service';
import { TransactionLogModel } from 'transaction-dash';
import { UserSubscriptionModel_ext } from './models/userSubscription';
import { IMenuBarItem, StarbaseMenuBarComponent } from 'starbase-menu-bar';
import { TimeZone, usTimezones } from './contact/admin-dashboard.component';
import { Observable, of, timeout } from 'rxjs';
import { SubscriptionService } from './services/subscription.service';
import { ContactRequestService } from './services/contact-request.service';
import { ContactRequestModel } from 'contact-us-form/lib/contact-requestModel';
import { ContactUsFormComponent } from 'contact-us-form';
import { IExternalScript } from './IExternalScripts';
import { FortisSubscriptionService } from './services/fortis-subscription.service';
import { BreakpointService, BreakpointState} from './breakpoint.service';
import { WebSocketService } from './services/web-socket.service';
import { KeywordDataService } from './services/keyword-data.service';
import { ImageFile } from './models/imageFile';
import { ImageService } from './services/image.service';
import { UserImageUpdateService } from 'contact-dash';

class ApiVersionInfo {
  public apiVersion: string;
  public starbaseVersion: string;
}

export const CONTACTUS_REPORT_PROBLEM = 'Report a Problem';
export const CONTACTUS_SUGGESTION = 'Suggest Content';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css', './fancyinput.css'],
  animations: [routeFade],
  encapsulation: ViewEncapsulation.Emulated
})
export class AppComponent implements OnInit, OnDestroy {

  public static externalScriptStore: IExternalScript[] = [
    { name: environment.squareApi.testing.scriptName, src: environment.squareApi.testing.scriptUrl },
    { name: environment.squareApi.production.scriptName, src: environment.squareApi.production.scriptUrl }
  ];

  public static rtfTools = {
    items: [
      'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
      'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
      'LowerCase', 'UpperCase', '|',
      'Undo', 'Redo', '|',
      'Formats', 'Alignments', '|',
      'OrderedList', 'UnorderedList', '|',
      'Indent', 'Outdent', '|',
      'CreateLink', 'CreateTable', '|',
      'ClearFormat', 'Print', 'SourceCode', '|',
      {
        tooltipText: 'Insert anchor for the currently active image in the bound set.',
        template: `<button type="button" class="e-tbar-btn e-btn" tabindex="0"
         id="custom_anchor_tbar" style="width:100%;"><div class="e-tbar-btn-text"
          style="font-weight: 500;"><i class="fas fa-anchor"></i></div></button>`
      }, '|',
      'FullScreen'
    ]
  };

  public static rtfSize = {
    width: '40px',
    items: [
      { text: '8', value: '8pt' },
      { text: '10', value: '10pt' },
      { text: '12', value: '12pt' },
      { text: '14', value: '14pt' },
      { text: '36', value: '36pt' },
      { text: '42', value: '42pt' }
    ]
  };

  public static rtfFontFamilies = { // for RTF component's font choices
    width: '65px',
    items: [
      { text: 'Research Arsenal', value: 'Montserrat' },
      { text: 'Segoe UI', value: 'Segoe UI' },
      { text: 'Arial', value: 'Arial,Helvetica,sans-serif' },
      { text: 'Courier New', value: 'Courier New,Courier,monospace' },
      { text: 'Georgia', value: 'Georgia,serif' },
      { text: 'Impact', value: 'Impact,Charcoal,sans-serif' },
      { text: 'Calibri Light', value: 'CalibriLight' }
    ]
  };

  /** The email address whence system emails send */
  emailFrom: string = environment.testMode ? environment.testSupportEmailAddress : environment.supportEmailAddress;
  isHandset = false;
  cancelMoney = false;
  cancelContent = false;
  cancelFound = false;
  cancelOther = false;
  loadingAvatar = false;
  private _subscription;

  constructor(private appGlobalsSvc: AppGlobalsService,
              private loginSvc: StarbaseLoginService,
              private modalSvc: StarbaseModalDialogService,
              private router: Router,
              private overlaySvc: StarbaseModalOverlayService,
              private galleryTypeSvc: GalleryTypeService,
              private lookupSvc: LookupDataService,
              private notiSvc: StarbaseNotificationService,
              private appInsights: AppInsightsService,
              private reqSvc: ContactRequestService,
              private helpDeskSvc: HelpDeskService,
              private subscriptionPlanSvc: LuSubscriptionPriceService,
              private fortisSubsSvc: FortisSubscriptionService,
              private breakpointService: BreakpointService,
              private imageSvc: ImageService,
              protected userSvc: UserDataService,
              protected contactSvc: ContactDataService,
              protected dashSubSvc: SubscriptionService,
              protected sbCmpMsgSvc: StarbaseComponentMessageService,
              public txnLogSvc: TransactionLogService,
              protected logSvc: LogDataService,
              private webSocketSvc: WebSocketService,
              private keywordSvc: KeywordDataService,
              private cd: ChangeDetectorRef, protected imageUpdateSvc: UserImageUpdateService) {
    this.version = this.appGlobalsSvc.appVersion;
    document.title = this.title;
    this.appInsights.logPageView('App loaded', '/');
    this.titleColor = environment.testMode ? '#e5e6e7' : '#e5e6e7'; // change the title text to reddish-brown if in testing mode.
    this.currentYear = new Date().getFullYear();
    this.usTimezones = usTimezones();
    this.txnLogHistFilter = new FilterParams<TransactionLogModel>();
    this.userTimeZone$ = of(usTimezones().find(t => t.name === Intl.DateTimeFormat().resolvedOptions().timeZone) ||
      {
        name: 'Coordinated Universal Time (UTC)',
        offset: 'UTC',
        dstOffset: 'UTC'
    });
    this.userTimeZone = usTimezones().find(t => t.name === Intl.DateTimeFormat().resolvedOptions().timeZone) ||
    {
      name: 'Coordinated Universal Time (UTC)',
      offset: 'UTC',
      dstOffset: 'UTC'
    };
    this.userDST = AppComponent.isDSTObservedToday();
  }

  public static isDSTObservedToday(): boolean {
    const stdTimezoneOffset = () => {
      const jan = new Date(0, 1);
      const jul = new Date(6, 1);
      return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
    };
    return new Date().getTimezoneOffset() < stdTimezoneOffset();
  }  /* eslint-disable @typescript-eslint/member-ordering */
  reloadRequired = false;
  currentSliderDisplayValue = 0.5;

  public appMinimumPwdScore = this.appGlobalsSvc.minimumPwdScore;
  public appMiniumPwdLength = this.appGlobalsSvc.minimumPwdLength;

  paymentModal: StarbaseModalOptions;

  public get isAdmin(): boolean {
    return this.loginSvc.isLoggedIn() && this.loginSvc.getUserModel().isAdmin();
  }
  testMode = environment.testMode;
  title = this.appGlobalsSvc.appTitle + (this.testMode ? ' — TESTING' : '');
  titleColor: string;
  version: string = environment.VERSION;
  // versionBeta: boolean = environment.testMode;
  // versionSuffix = (this.versionBeta ? 'beta - ' : ' - ') + environment.packageDate;
  @ViewChild('mainMenuBar', { static: true }) menuBar: StarbaseMenuBarComponent;
  @ViewChild('notiHistory', { static: true }) notificationHistoryBar: StarbaseNotificationHistoryComponent;
  @ViewChild('testingSiteModal', { static: true }) testingSiteModalTemplate: TemplateRef<any>;
  @ViewChild('helpAboutModal', { static: true }) helpAboutModalTemplate: TemplateRef<any>;
  @ViewChild('optionsModal', { static: true }) optionsModalTemplate: TemplateRef<any>;
  @ViewChild('optionsModalMobile', { static: true }) optionsModalMobileTemplate: TemplateRef<any>;
  @ViewChild('acctMgmtWrapper', { static: true }) accountMgmtCmp: TemplateRef<any>;
  @ViewChild('recordHistoryModal', { static: true }) recordHistoryModalTemplate: TemplateRef<any>;
  @ViewChild('paymentModal', { static: true }) paymentModalTemplate: TemplateRef<any>;
  @ViewChild('paymentMethodModal', { static: true }) paymentMethodModalTemplate: TemplateRef<any>;
  @ViewChild('cancelSubModal', { static: true }) cancelSubModalTemplate: TemplateRef<any>;
  @ViewChild('subscriptionPlanChoiceModal', { static: true }) subPlanChoiceTemplate: TemplateRef<any>;
  @ViewChild('donePaymentBtnTemplate', { static: true }) donePaymentBtnTemplate: TemplateRef<any>;
  @ViewChild('basicOkayBtnTemplate', { static: true }) basicOkayBtnTemplate: TemplateRef<any>;
  @ViewChild('logoutBody', { static: true }) logoutModalBody: TemplateRef<any>;
  // @ViewChild('helpDesk') helpDeskCmp: StarbaseHelpDeskComponent;

  public logoPath: string;
  private defaultLogoPath = '../assets/ra-logo.png';
  private loginLogoPath = '../assets/login-logo.png';
  public loggedInUser: IUserTokenModel = null;
  public currentUserAccount: IUserAccountModel = null;
  public displayName = '';
  public overlayEnabled = false;
  public galleryPage = '';
  public currentRoute = '';
  currentRouteId: string;
  public currentAccountTab = AccountTabs.Account;
  recordHistoryCols = Log_ext.LogColumns(false, false, false);
  recordHistoryData: FilterParams<Log_ext>;
  recordHistoryViewOpts = new NewTableEditorUserViewOptions(true);
  openOnUser = false;
  editUserId: any;
  currentUserContact: ContactModel;
  avatarCss: object;
  showNavMenu = false;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  OVCAvatarImage;
  isAvatarReady = false;
  pathExists = false;
  avatarPath: string;
  hoverAvatar = false;
  fileUploadMode = false;
  fileUploadActive = false;
  contactUsModalOpen = false;
  contactUsMessage: string;
  testingModal;
  contactUsSubjects = [
    ...ContactUsFormComponent.defaultSubjects,
    { name: 'Subscription Question', selected: false },
    { name: 'Library Question', selected: false },
    { name: 'Make a Research Contribution', selected: false }
  ];
  suggestContentSubjects = [
    { name: 'Suggest Edit', select: false },
    { name: 'Suggest Correction', selected: false },
    { name: 'Suggest Addition', selected: false },
    { name: 'Other', selected: false }
  ];
  suggectContentImageInfo = null;
  microfilmSearchActive = false;

  cancellationReason = '';

  private myAccountMenuBarItem: IMenuBarItem = {
    displayText: 'My Account',
    enabled: true,
    iconClass: 'fa-user',
    clickAction: () => {
      this.showOptions(true);
    },
    tooltip: 'View your subscription and more'
  };
  private helpMenuBarItem: IMenuBarItem = {
    displayText: 'Contact Us',
    enabled: true,
    iconClass: 'fa-comment-lines',
    clickAction: () => {
      this.launchContactUsForm(`Contact ${this.title} Staff`);
    },
    tooltip: 'Get help from our team'
  };
  private infoMenuBarItem: IMenuBarItem = {
    displayText: 'Info',
    enabled: true,
    iconClass: 'fa-info-circle',
    clickAction: () => this.showHelp()
  };
  private aboutUsMenuBarItem: IMenuBarItem = {
    displayText: 'About Us',
    enabled: true,
    iconClass: 'fa-users',
    clickAction: () => this.showAboutUs()
  };

  txnLogCols = TransactionLogModel.TableColumns(false);
  txnLogViewOpts = new NewTableEditorUserViewOptions(true, true, true, false, false);
  txnLogHistFilter: FilterParams<TransactionLogModel>;
  requestedTxnHist = false;

  formatAsDecimal = FormatDecimalString;
  usTimezones: TimeZone[];

  // helpDataActiveSet: HelpDataModel[] = [];
  helpDisplayed = false;
  currentYear: number;
  dashSubs$: Observable<{ module: string; subName: string; subscribed: boolean }[]>;
  glassMode = false;
  darkMode = false;
  tooltipDelay = 500;
  public userTimeZone$: Observable<TimeZone>;
  userTimeZone: TimeZone;
  userDST: boolean;
  notificationStyleMode = NotificationStyleMode.PopdownMode;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  CONTACTUS_REPORT_PROBLEM = CONTACTUS_REPORT_PROBLEM;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  CONTACTUS_SUGGESTION = CONTACTUS_SUGGESTION;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  RAred = '';

  userAccountOptions: string[] = ['Site Preferences', 'User Settings'];
  selectedUserAccountOption = 'Site Preferences';

  today = new Date();
  subPlan: LU_SubscriptionPriceModel_ext;
  public subUserAcct: UserAccountModel; // to use when working on subscriptions

  get isTrialUser(): boolean {
    return this.loggedInUser.isInUserGroup('Trial');
  }

  public static removeMarkupFromString(target: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = target;
    return tmp.textContent.replace(/<.*?>/g, '') || tmp.innerText || '';
  }

  ngOnInit() {
    /* eslint-disable no-console */
    console.info('Loading Research Arsenal (ngResearchArsenal-webapp) ' + this.version);

    //handle menubar dropdowns incase the user navigates away from gateway page.
    this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
        this.menuBar.showNavMenu = false;
        this.menuBar.showUserAccountMenu = false;
      }
    });

    this.imageUpdateSvc.userImagesChanged$.subscribe((user) => this.handleAvatarChange(user));

    if (isDevMode()) {
      console.warn('The application is running in dev mode.');
    }

    //web or handset subscription through breakpoint service.
    this._subscription = this.breakpointService.handset$.subscribe(
      isHandset => {
        this.isHandset = isHandset;
      }
    );

    this.appGlobalsSvc.triggerContactRequestEmitter.subscribe((request) => {
      this.launchContactUsForm(request.title, request.data);
    });
    this.appGlobalsSvc.triggerJoinNowFormEmitter.subscribe(() => this.handleJoinNowPath(this.subUserAcct));

    this.userSvc.deleteSubscriberUserGroupTrigger$.subscribe((id) => this.handleSubscriberUserGroupLinkDeletion(id));

    this.galleryTypeSvc.repairLibraryImages.subscribe((params) => this.handleLibraryImagesRepair(params));

    this.testingModal =  new StarbaseModalOptions(
      'Research Arsenal testing mode',
      ``,
      [], StarbaseModalTypes.Notice, this.testingSiteModalTemplate);

    this.testingModal.forcedWidth = this.isHandset? '95vw' : '';


    this.lookupSvc.getRequisiteData<ApiVersionInfo>(this.lookupSvc.getApiVersionDataUrl, '', 4).subscribe((d: ApiVersionInfo) => {
      this.version
        += '. \nAPI version: '
        + d.apiVersion
        + '. \nStarbase Core version: '
        + d.starbaseVersion;
      console.info(`Connected to API server ${d.apiVersion}; running Starbase version ${d.starbaseVersion}`);
      if (d.apiVersion.includes('test') || d.apiVersion.includes('dev')) { // inform the user of test mode.
        this.modalSvc.postModal(
          this.testingModal);
      }
    }, e => {
      console.error(e);
      this.modalSvc.postModal(
        new StarbaseModalOptions(
          'Starbase server communications error', 'There was a problem communicating with the server. ' + e, [], StarbaseModalTypes.Error));
    });
    if (this.loginSvc.isLoggedIn()) {
      this.getUserInfoFromSvc();
      this.webSocketSvc.setupUserConnection();
      this.keywordSvc.getAllSynonymAndChildrenData();
    }
    this.loginSvc.loginState.subscribe((s: boolean) => {
      if (s === true) {
        this.getUserInfoFromSvc();
        this.webSocketSvc.setupUserConnection();
        this.keywordSvc.getAllSynonymAndChildrenData();
      } else {
        this.modalSvc.clearSession();
        this.displayName = '';
        this.loggedInUser = null;
        this.router.navigateByUrl('login/out');
      }
    });
    this.modalSvc.modalEmitter.subscribe(m => {
      if (m !== null) {
        this.overlaySvc.enableOverlay();
      } else {
        this.overlaySvc.disableOverlay();
      }
    });
    this.overlaySvc.overlayEmitter.subscribe(o => {
      this.overlayEnabled = o;
    });
    this.sbCmpMsgSvc.recordHistoryEmitter.subscribe((guid: string) => this.handleRecordHistoryRequest(guid));
    this.contactSvc.triggerOpenUserAccount.subscribe(
      (id) =>  {
        this.editUserId = id;
        this.requestedTxnHist = false;
        this.manageUser();
      }
    );
    this.galleryPage = this.galleryTypeSvc.writeGalleryPageText();
    this.galleryTypeSvc.galleryTypeEmitter.subscribe(
      () => this.galleryPage = this.galleryTypeSvc.writeGalleryPageText());

    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe(() => {
      this.currentRoute = this.router.url;
      const routeSplit = this.router.url.split('/');
      this.currentRouteId = routeSplit[routeSplit.length - 1];
      if (this.currentRoute.includes('login')) {
        this.logoPath = this.loginLogoPath;
      } else {
        this.logoPath = this.defaultLogoPath;
      }
      if (isDevMode()) {
        console.log('Current route changed: ' + this.currentRoute);
      }

      this.microfilmSearchActive = this.router.url.includes('microfilm');
    });

    this.menuBar.menuBarItems = [
      this.myAccountMenuBarItem,
      this.menuBar.defaultMenuBarItems[0],
      this.menuBar.defaultMenuBarItems[1],
      this.menuBar.defaultMenuBarItems[2],
      // this.menuBar.defaultMenuBarItems[3],
      this.helpMenuBarItem,
      this.aboutUsMenuBarItem,
      this.infoMenuBarItem,
      this.menuBar.defaultMenuBarItems[5],
      this.menuBar.defaultMenuBarItems[6]
    ];

    this.userTimeZone$ = this.appGlobalsSvc.getStoredTimeZone().pipe(
      map(t => {
        if (!t) {
          return usTimezones().find(
            z => z.name === Intl.DateTimeFormat().resolvedOptions().timeZone)
            ||
          {
            name: 'Coordinated Universal Time (UTC)',
            offset: 'UTC',
            dstOffset: 'UTC'
          };
        } else {
          return t;
        }
      }),
      tap(x => this.userTimeZone = x)
    );
    this.userTimeZone$.subscribe(tz => this.userTimeZone = tz);    this.tooltipDelay = this.appGlobalsSvc.getStoredTooltipDelay() || 500;
    this.currentSliderDisplayValue = this.tooltipDelay / 1000; // Initialize display value
    // this.tooltipDelaySub.next({
    //   showDelay: this.tooltipDelay,
    //   hideDelay: 0,
    //   touchendHideDelay: 1000
    // });
    this.darkMode = this.appGlobalsSvc.getStoredDarkModeSetting();

    window.onbeforeunload = function() {
      this.ngOnDestroy();
    };
  }

  ngOnDestroy(): void {
    this.webSocketSvc.disconnectUserConnection();
    if(this._subscription){
      this._subscription.unsubscribe();
    }
  }

  get devOrTestingMode(): boolean {
    return isDevMode() || environment.testMode;
  }

  private getUserInfoFromSvc() {
    this.loggedInUser = this.loginSvc.getUserModel();
    this.displayName = this.loggedInUser.displayName;
    this.appGlobalsSvc.currentLoggedInUser = this.userSvc.getData(this.userSvc.GetMyAccountUri, '');
    this.appGlobalsSvc.currentLoggedInContact = this.appGlobalsSvc.currentLoggedInUser.pipe(map(
      user => user.contact
    ));

    this.contactSvc.getCurrentUser().subscribe(user => {
          this.currentUserContact = user;
          this.OVCAvatarImage = {backgroundImage: `url(${this.getLogoPath()})`};
          this.isAvatarReady = true;
          this.handleAvatarChange(user);
          this.cd.detectChanges();
        }
      );

    this.loadSubPlanPrices();

    // if (this.isHandset) {
      // eslint-disable-next-line max-len
      // this.modalSvc.postModal(new StarbaseModalOptions('Warning', 'Research Arsenal does not support mobile platforms.', undefined, StarbaseModalTypes.Warning));
    // }
  }

  public doHoverAvatar(yn: boolean): void{
    this.hoverAvatar = yn;
    this.cd.detectChanges();
  }

  public onSelectionChange(option: string) {
    this.selectedUserAccountOption = option;
  }

  public toggleDropdown(compName) {
    // eslint-disable-next-line eqeqeq
    if (compName == 'nav') {
      this.showNavMenu = !this.showNavMenu;
    }
  }

  public handleAvatarChange(user: ContactModel) {
    this.currentUserContact = user;
    this.OVCAvatarImage = {backgroundImage: `url(${this.getLogoPath()})`};
    this.isAvatarReady = true;
    this.cd.detectChanges();
  }

  public editAvatar(){
    if (this.fileUploadActive) {
      return;
    } else {
      this.fileUploadActive = true;
    }
    this.cd.detectChanges();
    this.fileUploadMode = true;
    this.manageUser();
  }

  private getLogoPath(): string {
    if (this.currentUserContact.picturePath){
      const separator = this.currentUserContact.picturePath.includes('?') ? '&' : '?';
      return `${this.currentUserContact.picturePath}${separator}_cb=${Date.now()}`;
    } else {
      return '/assets/def1.png';
    }
  }

  private loadSubPlanPrices(): void {
    this.subscriptionPlanSvc.getAllData(this.subscriptionPlanSvc.getActiveSubscriptionsUri).subscribe(
      (data) => this.appGlobalsSvc.allSubscriptionPlans = [
        data.find(p => p.name === 'Monthly'),
        data.find(p => p.name === 'Annual'),
        data.find(p => p.name === 'Student'),
        data.find(p => p.name === 'Daily (new)' || p.name === 'Weekly')
      ]
    );
  }

  getCurrentUserContact(){

    return this.currentUserContact;
  }

  toggleNotificationHistory(): void {
    this.notificationHistoryBar.showSessionNotifications();
  }
  hideOverlays(): void {
    this.notificationHistoryBar.showSessionHistory = false;
  }
  logOut() {
    this.webSocketSvc.disconnectUserConnection();
    this.loginSvc.logOut();
  }
  /** Function for User Management */
  manageUser(user: UserAccountModel  = null) {
    if (user) {
      this.editUserId = user.uid;
    }
    
    const closeHandler = () => {
      if (this.fileUploadMode) this.fileUploadMode = false;
      if(this.fileUploadActive) this.fileUploadActive = false;
      if(this.modalSvc.isCurrentlyModal()) this.modalSvc.closeModal('User Clicked Okay');
    };
    
    const btn = new StarbaseModalButton( 'Okay', closeHandler);
    const modalOptions = new StarbaseModalOptions('Manage User','', [btn], StarbaseModalTypes.Info,this.accountMgmtCmp);

    modalOptions.dismissable = true;
    modalOptions.forcedWidth = '1100px';
    this.requestedTxnHist = false;
    this.modalSvc.postModal(modalOptions);
    const sub = this.modalSvc.modalEmitter.subscribe(_ => {
      closeHandler();
      sub.unsubscribe();
    })
  }

  openPaymentMethod(user: UserAccountModel) {
    let queueModal = null;
    if (this.modalSvc.isCurrentlyModal()) {
      const modalHist = this.modalSvc.getSessionModals();
      queueModal = modalHist[modalHist.length - 1]; // get the current modal dialog, so we can return to it
    }
    this.modalSvc.closeModal('User opened a new modal (payment method)');
    this.subUserAcct = user;
    const paymentMethodModal =
      new StarbaseModalOptions(
        'Update payment method',
        '',
        [
          new StarbaseModalButton(
            'Done',
            () => {
              this.modalSvc.postModal(queueModal);
              this.modalSvc.closeModal('User closed payment method modal');
            }
          )
        ],
        null,
        this.paymentMethodModalTemplate
      );
    paymentMethodModal.buttons[0].overrideButtonTemplate = this.donePaymentBtnTemplate;
    paymentMethodModal.forcedWidth = this.isHandset ? '54vh' : '525px';
    this.modalSvc.postModal(
      paymentMethodModal
    );
  }

  subscribeBtnText(userAccount): string {
    if (userAccount.subscribed) {
      return 'Upgrade plan';
    } else if (userAccount.isSubscriptionActive) {
      return 'Resubscribe';
    } else {
      return 'Subscribe now';
    }
  }

  openPaymentForm(user: UserAccountModel, plan: LU_SubscriptionPriceModel_ext) {
    let queueModal = null;
    if (this.modalSvc.isCurrentlyModal()) {
      const modalHist = this.modalSvc.getSessionModals();
      queueModal = modalHist[modalHist.length - 1]; // get the current modal dialog, so we can return to it
    }
    this.modalSvc.closeModal('User opened a new modal (payment form)');
    this.subUserAcct = user;
    this.subPlan = plan;
    this.paymentModal = new StarbaseModalOptions(
      'Purchase a subscription to Research Arsenal',
      '',
      [
        new StarbaseModalButton(
          // this.sqSubsSvc.subscriptionUpdated ? 'Done' : 'Cancel',
          'Close',
          () => {
            if (this.fortisSubsSvc.subscriptionUpdated) {
              this.modalSvc.closeModal('User closed subscription modal');
              if (this.isAdmin === false) {
                this.loginSvc.setMessage('Please log in again to activate your subscription.');
                this.notiSvc.postNotification(
                  this.notiSvc.notificationBuilder(
                    'Please log in again',
                    'To activate your subscription, you must log in again.',
                    undefined, 9000,
                    StarbaseNotificationSentiment.Info
                  )
                );
                this.enableLogoutModal(`<p>In order to provide subscriber access to the site, you will need to be logged out. Press Logout to be logged out.</p>`,
                  'Logged out successfully',
                  '').subscribe(
                    (a: boolean) => {
                      if (a === true) {
                        this.loginSvc.logOut();
                      }
                    },
                    () => {
                      this.loginSvc.logOut();
                    }
                  );
              } else {
                this.notiSvc.postNotification(
                  this.notiSvc.notificationBuilder(
                    'Subscription purchased',
                    'To activate subscription, user must log out and in again.',
                    undefined, 9000,
                    StarbaseNotificationSentiment.Info
                  )
                );
                this.enableLogoutModal(`<p>In order to apply your subscriber benefits, you will need to log back in. Press Continue to proceed to the Login page.</p>`,
                  'Logged out successfully',
                  '').subscribe(
                    (a: boolean) => {
                      if (a === true) {
                        this.loginSvc.logOut();
                      }
                    },
                    () => {
                      this.loginSvc.logOut();
                    }
                  );
              }
            } else {
              this.modalSvc.postModal(queueModal);
              this.modalSvc.closeModal('User canceled subscription modal');
            }
          }
        )
      ],
      null,
      this.paymentModalTemplate
    );
    this.paymentModal.forcedWidth = '695px';
    this.paymentModal.dismissable = false;
    this.modalSvc.postModal(
      this.paymentModal
    );
  }

  showOptions(onUser: boolean = false) {
    if (onUser) {
      this.openOnUser = true;
      console.info('Account options requested');
    } else {
      this.openOnUser = false;
      console.info('Options requested');
    }
    this.requestedTxnHist = false;
    this.editUserId = null;
    const modalOptions = new StarbaseModalOptions(
      'Options',
      '',
      [
        new StarbaseModalButton(
          'Okay',
          () => this.modalSvc.closeModal('Closing options')
        )
      ],
        StarbaseModalTypes.Notice,
        this.isHandset ? this.optionsModalMobileTemplate : this.optionsModalTemplate
    );    modalOptions.forcedWidth = this.isHandset ? '95%':'75%';
    modalOptions.forcedHeight = this.isHandset ? '95%':'40rem';
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.isHandset ? null : modalOptions.buttons[0].overrideButtonTemplate = this.basicOkayBtnTemplate;
    console.log(this.modalSvc.getSessionModals());
    this.modalSvc.postModal(modalOptions);
    
    setTimeout(() => {
      this.cd.detectChanges();
    }, 100);
  }

  showSubscribeMessage(allowClose: boolean = true) { // nag user prompt
    const nagMsg = this.notiSvc.notificationBuilder(
      'Subscription required',
      'Subscribe now to see detailed image data and unlock many other features!',
      undefined, 8000,
      StarbaseNotificationSentiment.Warning
    );
    this.notiSvc.postNotification(nagMsg);
    this.userSvc.getData(this.userSvc.GetMyAccountUri).subscribe(
      (d: UserAccountModel) => this.handleSubscriptionManage(d, '', allowClose));
  }

  score = (pwd: string) => this.userSvc.getRequisiteData(this.userSvc.PasswordScoreUri + pwd).pipe(
    debounceTime(1000),
    map(s => s as PasswordScoreEnum)
  );

  handleSubscriptionCancel(user: UserAccountModel) {
    this.userSvc.sendAttemptedCancellationEmail().subscribe(_ => console.log('Cancel Button Clicked'));
    this.subUserAcct = user;
    let queueModal = null;
    if (this.modalSvc.isCurrentlyModal()) {
      const modalHist = this.modalSvc.getSessionModals();
      queueModal = modalHist[modalHist.length - 1]; // get the current modal dialog, so we can return to it
    }
    this.modalSvc.closeModal('User opened a new modal (sub cancel)');
    const cancelSubModal = new StarbaseModalOptions(
      'Cancel subscription to Research Arsenal?',
      '',
      [
        new StarbaseModalButton(
          'No, go back',
          () => {
            this.modalSvc.postModal(queueModal);
            this.modalSvc.closeModal('User exited cancellation modal');
          }),
          new StarbaseModalButton(
            'Yes, cancel my subscription',
            () => {
              if (!this.cancelMoney && !this.cancelContent && !this.cancelFound && !(this.cancelOther && this.cancellationReason)) {
                // eslint-disable-next-line max-len
                this.notiSvc.postNotification(this.notiSvc.notificationBuilder('Reason Required', 'Cancellation reason is required.', StarbaseNotificationTypes.Warning));
              } else {
                this.cancelSub();
                this.modalSvc.closeModal('User confirmed cancellation modal');
              }
            }
          )
      ],
      StarbaseModalTypes.Warning,
      this.cancelSubModalTemplate
    );
    cancelSubModal.forcedWidth = this.isHandset ? '54vh' :'80em';
    this.modalSvc.postModal(cancelSubModal);
  }

  handleSubscriptionManage(user: UserAccountModel, promoText: string = '', allowClose: boolean = true): void {
    this.subUserAcct = user;
    this.requestedTxnHist = false;
    // present user with subscription options here
    let queueModal = null;
    if (this.modalSvc.isCurrentlyModal()) {
      const modalHist = this.modalSvc.getSessionModals();
      queueModal = modalHist[modalHist.length - 1]; // get the current modal dialog, so we can return to it
      this.modalSvc.closeModal('User opened a new modal (subscription selection)');
    }
    const subChoiceModal = new StarbaseModalOptions(
      (user.subscriptionRecordUID && user.subscriptionRecordUID > 0 ? 'Change' : 'Choose') + ' your subscription plan',
      promoText,
      [
        new StarbaseModalButton(
          allowClose ? 'Cancel' : 'Back to Gallery',
          () => {
            this.modalSvc.postModal(queueModal);
            this.modalSvc.closeModal('User canceled subscription choice');
            if (!allowClose) {
              this.router.navigateByUrl('/library');
            }
          })
      ],
      StarbaseModalTypes.Info,
      this.subPlanChoiceTemplate
    );
    subChoiceModal.forcedWidth = this.isHandset ? '54vh' :'1090px';
    subChoiceModal.dismissable = allowClose;
    this.modalSvc.postModal(
      subChoiceModal
    );
    // this.openPaymentForm(user, new LU_SubscriptionPriceModel_ext().deserialize(user.subscriptionRecord.subscriptionPrice));
  }

  handleJoinNowPath(user?: UserAccountModel): void {
    if (user) {
      if (!user.subscribed) {
        this.showSubscribeMessage();
      } else {
        this.handleSubscriptionManage(user);
      }
    } else if (this.displayName !== '') { // logged in but don't have user account info
      this.userSvc.getData(this.userSvc.GetMyAccountUri).subscribe(
        (d: UserAccountModel) => this.handleSubscriptionManage(d)); // load latest account data
      this.userSvc.sendAttemptedUpgradeEmail().subscribe(_ => console.log('Upgrade Button Clicked'));
    }
    // otherwise we must be on login wrapper route and that component will listen for emitter.
  }

  private cancelSub() {
    this.notiSvc.postNotification(
      this.notiSvc.notificationBuilder(
        'Please wait',
        'Processing your request...',
        undefined, 7000,
        StarbaseNotificationSentiment.Info
      )
    );
    let reason = '';
    if (this.cancelMoney) {
      reason += 'Trying to save Money; ';
    }
    if (this.cancelContent) {
      // eslint-disable-next-line @typescript-eslint/quotes
      reason += "Not enough content/Didn't find what I was looking for; ";
    }
    if (this.cancelFound) {
      reason += 'Found everything I was looking for; ';
    }
    if (this.cancelOther) {
      reason += 'Other: ' + this.cancellationReason + ';';
    }

    this.appInsights.logEvent('Subscription cancellation', { user: this.subUserAcct.user, reason });
    this.reqSvc.cancellationReason(reason, this.subUserAcct.user).subscribe();
    this.fortisSubsSvc.cancelFortisSubscription(this.subUserAcct).subscribe(
      () => {
        this.notiSvc.postNotification(
          this.notiSvc.notificationBuilder(
            'Subscription canceled',
            'Cancellation was successful.',
            undefined, undefined,
            StarbaseNotificationSentiment.Info
          )
        );
        if (this.isAdmin === false) {
          this.logOut();
        }
      },
      (e) => {
        console.error(e);
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.appInsights.logError(e, { 'subscription cancel': 'failed', user: this.subUserAcct.user });
        this.modalSvc.postModal(
          new StarbaseModalOptions(
            'Subscription cancellation error',
            `There was an error while trying to cancel your subscription.
            Please contact the billing dept. at ${this.emailFrom}`,
            [], StarbaseModalTypes.Error
          )
        );
      });
  }

  handleSubscriberUserGroupLinkDeletion(id: string | number) {
    let queueModal = null;
    if (this.modalSvc.isCurrentlyModal()) {
      const modalHist = this.modalSvc.getSessionModals();
      queueModal = modalHist[modalHist.length - 1]; // get the current modal dialog, so we can return to it
    }
    this.modalSvc.closeModal('User opened a new modal (sub cancel)');
    const removeUserGroupModal = new StarbaseModalOptions(
      'Confirm Removal From Subscriber User Group',
      `This User Group may be attached to an existing subscription.
       Removing this may deny the user from accessing services they have paid for.
       Are you sure you want to remove the user from this group?`,
      [
        new StarbaseModalButton(
          'No',
          () => {
            this.modalSvc.postModal(queueModal);
            this.modalSvc.closeModal('User exited cancellation modal');
          }),
          new StarbaseModalButton(
            'Yes',
            () => {
              this.userSvc.deleteSubscriberUserGroupLink(id).subscribe(
                () => {
                  this.notiSvc.postNotification(
                    this.notiSvc.notificationBuilder(
                      'Success',
                      'User removed from User Group successfully',
                      undefined, undefined,
                      StarbaseNotificationSentiment.Success
                    )
                  );
                  this.cd.detectChanges();
                },
                (e) => {
                  console.error(e);
                  this.notiSvc.postNotification(
                    this.notiSvc.notificationBuilder(
                      'Error',
                      'Failed to remove User from User Group',
                      undefined, undefined,
                      StarbaseNotificationSentiment.Error
                    )
                  );
                  this.cd.markForCheck();
                }
              );
              this.modalSvc.postModal(queueModal);
              this.modalSvc.closeModal('User confirmed cancellation modal');
            }
          )
      ],
      StarbaseModalTypes.Warning
    );
    this.modalSvc.postModal(removeUserGroupModal);
  }

  requestTxnHistory(userUID: number): void {
    this.requestedTxnHist = true;
    this.txnLogHistFilter = new FilterParams<TransactionLogModel>();
    const fp = this.txnLogHistFilter;
    fp.rpp = 10;
    fp.addToFilter('UserSubscription.UserAccountUID', userUID);
    this.txnLogSvc.getDataByFilterParamsBroadSearch(this.txnLogSvc.broadSearchUri, fp).subscribe(
      (d) => {
        this.txnLogHistFilter = new FilterParams<TransactionLogModel>().deserialize(d);
        this.txnLogHistFilter.collection.forEach(t => new TransactionLogModel().deserialize(t));
      },
      () => this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Log history error',
          'Could not load the transaction log history for that user',
           undefined, undefined,
            StarbaseNotificationSentiment.Error)));
  }
  requestUserSubHistory(userSub: UserSubscriptionModel_ext): void {
    this.handleRecordHistoryRequest(userSub.guid);
  }

  handleRecordHistoryRequest(guid: string) {
    if (!guid || guid === '') {
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Unknown entity',
          'Sorry, record history is not available for that entity.',
          undefined, undefined,
            StarbaseNotificationSentiment.Notice
        )
      );
      return;
    }
    let queueModal = null;
    if (this.modalSvc.isCurrentlyModal()) {
      const modalHist = this.modalSvc.getSessionModals();
      queueModal = modalHist[modalHist.length - 1]; // get the current modal dialog, so we can return to it
      this.modalSvc.closeModal('User opened a new modal (record history)');
    }
    this.notiSvc.postNotification(
      this.notiSvc.notificationBuilder(
        'Record history',
        'Loading the logs. Please wait...'
      )
    );
    const fp = new FilterParams<Log_ext>();
    fp.rpp = 10;
    fp.addToFilter('EntityGuid', guid);
    // eslint-disable-next-line import/no-deprecated
    this.logSvc.getDataByFilterParamsBroadSearch(this.logSvc.broadSearchUri, fp).subscribe(d => {
      this.logSvc.setLastRetrievedEntityName((d.collection.length) ? d.collection[0].entityName : '');
      this.recordHistoryData = d;
      const modalGuidHist = new StarbaseModalOptions(
        'Record History',
        '',
        [
          new StarbaseModalButton(
            'Back',
            () => {
              this.modalSvc.postModal(queueModal);
              this.modalSvc.closeModal('User clicked back');
              this.openOnUser = false;
            })
          ],
          StarbaseModalTypes.Info,
        this.recordHistoryModalTemplate);
      modalGuidHist.forcedWidth = '75vw';
      this.modalSvc.postModal(modalGuidHist);
    },
    () => this.notiSvc.postNotification(
           this.notiSvc.notificationBuilder(
            'Server error',
            'Sorry, there was an error retrieving the data history.',
            undefined, undefined,
            StarbaseNotificationSentiment.Error
        )));
  }

  showHelp(): void {
    // this.helpDeskSvc.getTestData().subscribe(d => {
    //   this.helpDeskSvc.getData<HelpDataModel[]>(this.helpDeskSvc.GetHelpByViewName, this.currentRoute.replace('/', '')).subscribe(d => {
    //   this.helpDataActiveSet = d;
    //   this.helpDisplayed = true;
    //   this.helpDeskCmp.summonHelp();
    // });
    this.modalSvc.postModal(
      new StarbaseModalOptions(
        'Research Arsenal Site Info',
        '',
        [],
        StarbaseModalTypes.Info,
        this.helpAboutModalTemplate
      )
    );
  }

  hideHelpModal() {
    this.helpDisplayed = false;
    this.modalSvc.closeModal('Closed from help desk action.');
  }

  showAboutUs() {
    this.router.navigate(['/about']);
  }

  redirectToLib() {
    this.router.navigate(['/gateway']);
    this.menuBar.showNavMenu = false;
  }

  redirectToSearch() {
    this.router.navigate(['/search']);
    this.menuBar.showNavMenu = false;
  }

  openFAQ() {
    this.router.navigateByUrl('https://researcharsenal.com/faqs/');
  }

  numberOfUnreadNotifications(): number { // wrapper function for template
    return this.notiSvc.getNumberOfUnreadSessionNotifications();
  }

  /** Method to update main stylings for app scheme 'normal', 'dark', or 'glass' */
  setStyle(scheme: string): void {
    if (scheme === 'normal') {
      this.darkMode = false;
      this.glassMode = false;
    } else if (scheme === 'dark') {
      this.darkMode = true;
      this.glassMode = false;
    } else if (scheme === 'glass') {
      this.darkMode = false;
      this.glassMode = true;
    }
    this.appGlobalsSvc.setDarkMode(this.darkMode);
    this.appGlobalsSvc.setGlassMode(this.glassMode);
  }  /** Method to set tooltip delay for site. */
  setTooltipDelay(delay: number): void {
    this.tooltipDelay = delay * 1000;
    this.currentSliderDisplayValue = delay; // Update display value
    this.appGlobalsSvc.setTooltipDelay(this.tooltipDelay);
    this.reloadRequired = true;
    // Trigger change detection to update the display
    this.cd.detectChanges();
  }

  /** Get the current slider value in seconds for display */
  get currentSliderValue(): number {
    return this.tooltipDelay / 1000;
  }

  /** Format the slider value for display */
  formatSliderLabel = (value: number): string => {
    return `${value}s`;
  };

  /** Method to reset site to default preferences. */
  clearPrefs(): void {
    this.modalSvc.closeModal('App forced Options modal to close.');
    const yesBtn = new StarbaseModalButton('Reset',
      () => {
        if (!this.appGlobalsSvc.clearAll()) {
          this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
            'Error', 'Could not clear preferences.',
            undefined, undefined,
            StarbaseNotificationSentiment.Error
          ));
        } else {
          // eslint-disable-next-line no-console
          console.info('User preferences cleared from browser.');
          window.location.reload();
        }
        this.modalSvc.closeModal('User chose to reset preferences.');
      });
    const noBtn = new StarbaseModalButton('Cancel',
      () => {
        this.modalSvc.closeModal('User chose to cancel.');
      });
    this.modalSvc.postModal(new StarbaseModalOptions(
      'Reset Your Preferences?',
      `Are you sure you want to reset all ${this.title} preferences? This only affects
      preferences you've set for this app on this browser. If you are logged in anywhere else, those
      preferences will remain.`,
      [yesBtn, noBtn],
      StarbaseModalTypes.Warning
    ));
  }

  openDashboardSettings() {
    this.modalSvc.closeModal('Options modal was forced to close by application.');
    this.router.navigateByUrl('dashboard');
    this.appGlobalsSvc.callDashboardCustomizationsModal.next(true);
  }

  changeTimezone(zone: TimeZone) {
    this.appGlobalsSvc.setTimeZone(zone);
    this.userTimeZone = zone;
    return false;
  }

  raHomepage(): void {
    window.location.assign('https://researcharsenal.com');
  }

  loadSubs() {
    this.appGlobalsSvc.currentLoggedInUser.subscribe(u => {
      this.currentUserAccount = new UserAccountModel(u.contactUID, u.user, u.firstName, u.lastName, false).deserialize(u);
      this.dashSubs$ = this.dashSubSvc.loadSubscriptions(u.uid);
    });
  }

  loadUserAccount() {
    this.currentUserAccount = null;
    this.appGlobalsSvc.currentLoggedInUser.subscribe(u => {
      this.currentUserAccount = new UserAccountModel(u.contactUID, u.user, u.firstName, u.lastName, false).deserialize(u);
    });
  }

  launchContactUsForm(title: string, imageInfo = null) {
    this.contactUsMessage = title;
    if (title === CONTACTUS_REPORT_PROBLEM) {
      this.contactUsMessage = CONTACTUS_REPORT_PROBLEM;
      this.contactUsSubjects.find((val) => val.name === CONTACTUS_REPORT_PROBLEM).selected = true;
    }

    if (!this.loggedInUser) {
      this.loggedInUser = new UserTokenModel(this.loginSvc.getUserNameFromMemory(), this.displayName, '');
    }
    if (!this.loggedInUser.user || this.loggedInUser.user === '') {
      this.loggedInUser = undefined; // set to undefined as no valid user is logged in.
    }

    if (imageInfo) {
      this.suggectContentImageInfo = imageInfo;
    }

    this.contactUsModalOpen = true;
    this.overlaySvc.enableOverlay();
  }

  submitContactRequest(d: ContactRequestModel) {
    if (this.suggectContentImageInfo) {
      d.message += '\r\nFor Image: [' + this.suggectContentImageInfo.uid + '] ' + this.suggectContentImageInfo.name;
    }
    this.reqSvc.postData(this.reqSvc.postUri, d).subscribe(
      () => {
        this.suggectContentImageInfo = null;
        this.notiSvc.postNotification(this.notiSvc.notificationBuilder('Success', 'Request submitted', undefined, undefined,
        StarbaseNotificationSentiment.Success));
        this.contactUsModalOpen = false;
        this.overlaySvc.disableOverlay();
      },
      (e) => {
        this.suggectContentImageInfo = null;
        console.error(e);
        this.notiSvc.postNotification(this.notiSvc.notificationBuilder('Error', 'Failed to submit contact request', undefined, undefined,
        StarbaseNotificationSentiment.Error));
      }
    );
  }

  cancelContactRequest() {
    this.contactUsModalOpen = false;
    this.overlaySvc.disableOverlay();
    this.contactUsSubjects.forEach((s) => s.selected = false);
  }

  onPull() {
    location.reload();
  }

  handleLibraryImagesRepair(params: FilterParams<ImageFile>) {
    this.imageSvc.getDataByFilterParamsBroadSearch(
          this.imageSvc.broadSearchUri + (this.galleryTypeSvc.CivilianResearchMode ? '?civilianMode=true' : ''),
          params)
          .pipe(
            finalize(() => this.cd.markForCheck()))
          .subscribe(d => {
            if (isDevMode() || environment.testMode) {
              d.collection.map((i: ImageFile) => {
                i.thumbnailPath = i.thumbnailPath.replace('wwwroot/', ''); // fix local dev server hosting
              }
              );
            }
            this.galleryTypeSvc.setLibraryImages(d.collection);
          }, err => {
            this.notiSvc.postNotification(
              this.notiSvc.notificationBuilder(
                'Error',
                'Failed to Repair Library Image Data',
                undefined, undefined, StarbaseNotificationSentiment.Error));
              console.error(err);
          });
  }

  enableLogoutModal(msg: string, successmsg?: string, errormsg?: string): Observable<boolean> {
    const m = new Observable<boolean>(obj => {
      const modal = new StarbaseModalOptions('Login Required', msg, [
        new StarbaseModalButton('Continue', () => {
          obj.next(true);
          this.modalSvc.closeModal(successmsg);
          this.loginSvc.logOut();
        }),
      ], StarbaseModalTypes.Confirmation, this.logoutModalBody);
      modal.dismissable = false;
      this.modalSvc.postModal(modal);
    });

    return m;
  }


}
